import CheckBoxOutlineBlankOutlined from "@mui/icons-material/CheckBoxOutlineBlankOutlined";
import CheckBox from "@mui/icons-material/CheckBox";
import { Autocomplete, Box, Checkbox, Chip, Stack, TextField } from "@mui/material";
import type { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useSupabaseLoader } from "../../server/supabase/hooks";
import {
  SupabaseCustomEmailHeaderService,
  type CustomHeader,
} from "../channels/email/server/supabase-custom-email-header-service";
import type { MomentFormMoment } from "../moment/types/moment-form-moment";

export const CustomEmailHeaderSelect: FC<{ disabled?: boolean }> = ({ disabled }) => {
  const { control } = useFormContext<MomentFormMoment>();

  const { data: customHeaders, loading } = useSupabaseLoader(async ({ supabase, account_id }) => {
    const service = new SupabaseCustomEmailHeaderService(supabase);
    const resp = await service.getAll(account_id, {
      order: [{ column: "display_name" }],
    });
    if (resp.error) throw resp.error;
    return resp.data;
  }, []);

  return (
    <Stack direction="row" alignItems="center" spacing={1} pt={2}>
      <Controller
        name="custom_headers"
        control={control}
        render={({ field }) => {
          const headers = field.value ?? [];
          const selectedIds = new Set(headers.map((h) => h.id));
          const selectedHeaders = (customHeaders ?? []).filter((h) => selectedIds.has(h.id));

          return (
            <Autocomplete<CustomHeader, true>
              sx={{ width: 500 }}
              multiple
              limitTags={2}
              loading={loading}
              disabled={disabled}
              options={customHeaders ?? []}
              value={selectedHeaders}
              getOptionLabel={(option) => option.display_name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, newValue) => {
                field.onChange(
                  newValue.map((h) => ({
                    id: h.id,
                    header_name: h.header_name,
                    header_value: h.header_value,
                    display_name: h.display_name,
                  })),
                );
              }}
              disableCloseOnSelect
              renderOption={(props, option, { selected }) => (
                <Box component="li" {...props} key={option.id}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlankOutlined fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    checked={selected}
                    sx={{ mr: 1 }}
                  />
                  {option.display_name}
                </Box>
              )}
              renderValue={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id}
                    label={option.display_name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => <TextField {...params} variant="outlined" label="Mark as" />}
            />
          );
        }}
      />
    </Stack>
  );
};
